import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui';
import { Badge } from '@/components/ui';
import { Button } from '@/components/ui';
import { Progress } from '@/components/ui';
import { getStrategicAnalysis, executiveSummary39V, executiveSummaryCafeMax, executiveSummaryLePresident, StrategicProject } from '@/data/mocks/strategicAnalysis';
import {
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Target,
  CheckCircle,
  XCircle,
  ArrowRight,
  Zap,
  Euro
} from 'lucide-react';

interface StrategicOverviewProps {
  restaurantId: string;
}

export const StrategicOverview: React.FC<StrategicOverviewProps> = ({ restaurantId }) => {
  const projects = getStrategicAnalysis(restaurantId);
  const summary = restaurantId === '39v' ? executiveSummary39V : 
                  restaurantId === 'cafe-max' ? executiveSummaryCafeMax :
                  restaurantId === 'le-president' ? executiveSummaryLePresident : null;

  if (!summary) {
    return <div className="text-center text-gray-500">Analyse stratégique en cours...</div>;
  }

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score: number) => {
    if (score >= 70) return 'from-green-50 to-green-100';
    if (score >= 40) return 'from-yellow-50 to-yellow-100';
    return 'from-red-50 to-red-100';
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Résumé Exécutif Alarmiste */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="border-2 border-red-500 bg-gradient-to-r from-red-50 via-orange-50 to-yellow-50">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="flex-1">
                <CardTitle className="text-xl sm:text-2xl flex items-center gap-2 sm:gap-3">
                  <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
                  Résumé Exécutif
                </CardTitle>
                <CardDescription className="text-sm sm:text-base mt-2">
                  <span className="font-bold text-red-700">{summary.overallAssessment}</span>
                </CardDescription>
              </div>
              <Badge variant="destructive" className="text-sm sm:text-lg px-3 sm:px-4 py-1.5 sm:py-2 w-fit">
                Urgence {summary.urgencyLevel.toUpperCase()}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-6">
            {/* Findings Critiques */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              {summary.keyFindings.map((finding, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 bg-white/80 rounded-lg border border-red-200"
                >
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-gray-900">{finding}</span>
                </div>
              ))}
            </div>

            {/* Métriques d'impact */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 pt-4 border-t border-red-200">
              <div className="text-center p-4 bg-white/60 rounded-lg">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Perte de CA Estimée</p>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-red-700">{summary.estimatedLostRevenue}</p>
                <p className="text-xs text-gray-500 mt-1">par an</p>
              </div>

              <div className="text-center p-4 bg-white/60 rounded-lg">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Potentiel Quick Wins</p>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-green-700">{summary.quickWinsPotential}</p>
                <p className="text-xs text-gray-500 mt-1">en {summary.timeline}</p>
              </div>

              <div className="text-center p-4 bg-white/60 rounded-lg">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Euro className="w-4 h-4 sm:w-5 sm:h-5 text-stratos-gold" />
                  <p className="text-xs sm:text-sm font-medium text-gray-600">ROI Estimé</p>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-stratos-gold">{summary.estimatedROI}</p>
                <p className="text-xs text-gray-500 mt-1">en 12 mois</p>
              </div>
            </div>

            {/* CTA */}
            <div className="flex justify-center pt-4">
              <Link to="/proposal" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full bg-gradient-to-r from-stratos-gold to-stratos-gold-dark text-gray-900 hover:shadow-lg hover:shadow-stratos-gold/30"
                >
                  <Zap className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  <span className="text-sm sm:text-base">Voir la Proposition Pack {summary.recommendedPack}</span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Projets Stratégiques */}
      <div className="space-y-4 sm:space-y-6">
        <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2 sm:gap-3">
          <Target className="w-5 h-5 sm:w-6 sm:h-6 text-stratos-gold" />
          Axes Stratégiques Prioritaires
        </h2>

        {projects.map((project, index) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className={`border-l-4 ${
              project.currentState.score < 30 
                ? 'border-l-red-500' 
                : project.currentState.score < 60 
                ? 'border-l-yellow-500' 
                : 'border-l-green-500'
            }`}>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1">
                    <CardTitle className="text-lg sm:text-xl">{project.name}</CardTitle>
                    <CardDescription className="text-sm sm:text-base mt-2">
                      {project.objective}
                    </CardDescription>
                  </div>
                  <div className="text-center sm:text-right">
                    <div className={`text-3xl sm:text-4xl font-bold ${getScoreColor(project.currentState.score)}`}>
                      {project.currentState.score}/100
                    </div>
                    <Badge 
                      variant={
                        project.currentState.status === 'critical' 
                          ? 'destructive' 
                          : project.currentState.status === 'warning'
                          ? 'warning'
                          : 'success'
                      }
                      className="mt-2"
                    >
                      {project.currentState.status.toUpperCase()}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6">
                {/* Analyse Actuelle */}
                <div className={`p-3 sm:p-4 rounded-lg bg-gradient-to-r ${getScoreBg(project.currentState.score)}`}>
                  <h4 className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" />
                    État des Lieux
                  </h4>
                  <p className="text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
                    {project.currentState.analysis}
                  </p>
                  <div className="grid grid-cols-1 gap-2">
                    {project.currentState.findings.map((finding, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs sm:text-sm">
                        <span className="flex-shrink-0">{finding}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Opportunités */}
                <div className="bg-green-50 p-3 sm:p-4 rounded-lg border border-green-200">
                  <h4 className="text-sm sm:text-base font-semibold mb-3 flex items-center gap-2 text-green-800">
                    <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" />
                    Potentiel & Opportunités
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div>
                      <p className="text-xs text-green-700 mb-1">Potentiel</p>
                      <p className="text-sm font-semibold text-green-900">
                        {project.opportunities.potential}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-green-700 mb-1">Impact Estimé</p>
                      <p className="text-sm font-semibold text-green-900">
                        {project.opportunities.estimatedImpact}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-green-700 mb-1">Timeline</p>
                      <p className="text-sm font-semibold text-green-900">
                        {project.opportunities.timeline}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quick Wins */}
                <div>
                  <h4 className="text-sm sm:text-base font-semibold mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-stratos-gold" />
                    Actions Rapides (Quick Wins)
                  </h4>
                  <div className="grid grid-cols-1 gap-3">
                    {project.recommendations.quick_wins.map((action, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <CheckCircle className="w-4 h-4 text-stratos-gold flex-shrink-0 mt-0.5" />
                        <span className="text-xs sm:text-sm">{action}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* KPIs */}
                <div>
                  <h4 className="text-sm sm:text-base font-semibold mb-3">Objectifs Mesurables</h4>
                  <div className="space-y-3">
                    {project.kpis.map((kpi, i) => (
                      <div key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-white rounded-lg border">
                        <div className="flex-1">
                          <p className="text-xs sm:text-sm font-medium">{kpi.name}</p>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-1">
                            <span className="text-xs text-gray-500">
                              Actuel: <span className="font-semibold text-red-600">{kpi.current}</span>
                            </span>
                            <ArrowRight className="w-4 h-4 text-gray-400 hidden sm:block" />
                            <span className="text-xs text-gray-500">
                              Cible: <span className="font-semibold text-green-600">{kpi.target}</span>
                            </span>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs sm:ml-4 w-fit">
                          {kpi.gap}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Investment */}
                <div className="bg-stratos-gold/10 p-3 sm:p-4 rounded-lg border-2 border-stratos-gold/30">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-700">Investissement Recommandé</p>
                      <p className="text-base sm:text-lg font-bold text-gray-900 mt-1">
                        {project.recommendations.investment_needed}
                      </p>
                    </div>
                    <Link to="/proposal" className="w-full sm:w-auto">
                      <Button
                        variant="outline"
                        className="w-full border-stratos-gold text-stratos-gold-dark hover:bg-stratos-gold hover:text-gray-900"
                      >
                        Voir le Pack
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
